import { Component } from '@theme/component';

/**
 * Navigation for the "Choose your…" comparison when it is laid out as a carousel.
 *
 * The carousel itself is CSS — a scroll-snapping grid row — so this component only adds the two
 * things CSS cannot: arrows that move by one screenful, and dots that say how many screenfuls there
 * are and which one you are on. It measures instead of counting cards, so it stays correct when the
 * column count changes at a breakpoint, and it hides itself entirely when everything already fits.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} [nav]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [dots]
 *
 * @extends Component<Refs>
 */
class ProductCompareCarousel extends Component {
  requiredRefs = ['track'];

  #pages = 1;
  #index = 0;
  #frame = 0;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this.refs.track);

    this.#measure();
    this.#showCurrent();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.track.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  next() {
    this.go(this.#index + 1);
  }

  previous() {
    this.go(this.#index - 1);
  }

  /**
   * Scroll to one screenful, by index.
   * @param {number} index
   */
  go(index) {
    const { track } = this.refs;
    const page = Math.max(1, track.clientWidth);
    const target = Math.min(Math.max(index, 0), this.#pages - 1);

    track.scrollTo({
      left: target * page,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  /**
   * Open on the card of the product being viewed. Landing on "Mochila Roncal" while reading the XL
   * hides the one card that says "you are looking at this one".
   */
  #showCurrent() {
    const { track } = this.refs;
    const current = track.querySelector('.compare-item--current');
    if (!current || this.#pages < 2) return;

    const page = Math.max(1, track.clientWidth);
    const target = Math.floor((/** @type {HTMLElement} */ (current).offsetLeft) / page);
    if (target > 0) {
      track.scrollTo({ left: target * page, behavior: 'auto' });
      this.#sync();
    }
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  /** Recount the screenfuls — on resize, and once on load. */
  #measure() {
    const { track, nav, dots } = this.refs;
    const page = Math.max(1, track.clientWidth);

    // One extra pixel of tolerance: sub-pixel track widths otherwise report a phantom second page.
    this.#pages = Math.max(1, Math.ceil((track.scrollWidth - 1) / page));

    if (nav) nav.hidden = this.#pages < 2;

    if (dots && dots.childElementCount !== this.#pages) {
      dots.replaceChildren();

      for (let i = 0; i < this.#pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-compare__dot';
        dot.setAttribute('aria-label', `${dots.dataset.label ?? ''} ${i + 1}`.trim());
        dot.addEventListener('click', () => this.go(i));
        dots.append(dot);
      }
    }

    this.#sync();
  }

  /** Reflect the current scroll position in the arrows and the dots. */
  #sync() {
    const { track, previous, next, dots } = this.refs;
    const page = Math.max(1, track.clientWidth);

    this.#index = Math.min(Math.round(Math.abs(track.scrollLeft) / page), this.#pages - 1);

    if (previous) previous.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= this.#pages - 1;

    if (dots) {
      [...dots.children].forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.#index);
        dot.setAttribute('aria-current', i === this.#index ? 'true' : 'false');
      });
    }
  }
}

if (!customElements.get('product-compare-carousel')) {
  customElements.define('product-compare-carousel', ProductCompareCarousel);
}
